/**
 * Open Tasks Panel
 *
 * Shows onboarding tasks for players who haven't completed setup.
 * Displayed in the left sidebar when there are incomplete tasks.
 */

import { useEffect, useState } from 'react';
import { Icon } from '../Icon';
import { TaskCard } from './TaskCard';
import { useOnboardingStore, TaskId } from '../../stores/onboardingStore';
import { useCampaignStore } from '../../stores/campaignStore';
import { useCharacterStore } from '../../stores/characterStore';
import { useSessionStore } from '../../stores/sessionStore';
import { CharacterCreationWizard } from '../CharacterCreationWizard';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

export function OpenTasksPanel() {
  const currentCampaignId = useCampaignStore((s) => s.currentCampaignId);
  const {
    initCampaignTasks,
    getTasks,
    hasIncompleteTasks,
    completeTask,
  } = useOnboardingStore();

  const [showCharacterWizard, setShowCharacterWizard] = useState(false);

  const characters = useCharacterStore((s) => s.characters);
  const characterOwners = useCharacterStore((s) => s.characterOwners);
  const userId = useSessionStore((s) => s.userId);

  // Initialize tasks when campaign changes
  useEffect(() => {
    if (currentCampaignId) {
      initCampaignTasks(currentCampaignId);
    }
  }, [currentCampaignId, initCampaignTasks]);

  // Auto-complete "create_character" if the player already has one
  useEffect(() => {
    if (!currentCampaignId || !userId) return;
    const hasOwnCharacter = Array.from(characters.values()).some(
      (c) => !c.isNpc && characterOwners.get(c.id) === userId,
    );
    if (hasOwnCharacter) {
      completeTask(currentCampaignId, 'create_character');
    }
  }, [currentCampaignId, userId, characters, characterOwners, completeTask]);

  // Guard: while character ownership is still loading, suppress the task so
  // players with existing chars don't see "Create Character" flash in.
  const ownershipStillLoading = characters.size > 0 && characterOwners.size === 0;

  // Get tasks for current campaign
  const tasks = currentCampaignId ? getTasks(currentCampaignId) : [];
  const hasIncomplete = currentCampaignId ? hasIncompleteTasks(currentCampaignId) : false;

  // Handle task action
  const handleTaskAction = (taskId: TaskId) => {
    switch (taskId) {
      case 'create_character':
        setShowCharacterWizard(true);
        break;
      case 'set_avatar':
        // TODO: Open avatar picker
        break;
      case 'review_rules':
        // TODO: Open rules modal
        break;
    }
  };

  // Handle character created
  const handleCharacterCreated = () => {
    if (currentCampaignId) {
      completeTask(currentCampaignId, 'create_character');
    }
    setShowCharacterWizard(false);
  };

  // Don't render if no incomplete tasks, or while ownership is still loading
  if (!hasIncomplete || ownershipStillLoading) {
    return null;
  }

  const incompleteTasks = tasks.filter((t) => !t.completed);
  const completedTasks = tasks.filter((t) => t.completed);

  return (
    <>
      <div
        style={{
          padding: 'var(--spacing-md)',
          backgroundColor: 'var(--color-background)',
          borderRadius: 'var(--radius-md)',
          border: `1px solid ${GOLD_DIM}`,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-sm)',
            marginBottom: 'var(--spacing-md)',
            paddingBottom: 'var(--spacing-sm)',
            borderBottom: `1px solid ${GOLD_DIM}`,
          }}
        >
          <Icon name="scroll" size={18} color={GOLD} />
          <h3
            style={{
              margin: 0,
              flex: 1,
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-display)',
              color: GOLD,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            Open Tasks
          </h3>
          <span
            style={{
              padding: '2px 8px',
              backgroundColor: GOLD_DIM,
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-xs)',
              color: GOLD,
              fontWeight: 600,
            }}
          >
            {incompleteTasks.length}
          </span>
        </div>

        {/* Task list */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-sm)',
          }}
        >
          {incompleteTasks.map((task) => (
            <TaskCard key={task.id} task={task} onAction={handleTaskAction} />
          ))}

          {/* Show completed tasks (collapsed) */}
          {completedTasks.length > 0 && (
            <div style={{ marginTop: 'var(--spacing-xs)' }}>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  marginBottom: 'var(--spacing-xs)',
                }}
              >
                Completed
              </div>
              {completedTasks.map((task) => (
                <TaskCard key={task.id} task={task} onAction={handleTaskAction} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Character Creation Wizard */}
      {showCharacterWizard && (
        <CharacterCreationWizard
          onClose={() => setShowCharacterWizard(false)}
          onCreated={handleCharacterCreated}
        />
      )}
    </>
  );
}
